import { useId } from 'react';
import type { ReactNode } from 'react';

export const avatarCount = 10;

interface Palette {
  from: string;
  to: string;
  accent: string;
  second: string;
}

const face = '#111826';
const skin = '#0B111C';

const palettes: Palette[] = [
  { from: '#0A3A3E', to: '#05080D', accent: '#22E5D4', second: '#FF3DCB' },
  { from: '#3A0A36', to: '#05080D', accent: '#FF3DCB', second: '#FFE14D' },
  { from: '#1E0E44', to: '#05080D', accent: '#9B5CFF', second: '#22E5D4' },
  { from: '#06331E', to: '#05080D', accent: '#3DFF9A', second: '#22E5D4' },
  { from: '#082544', to: '#05080D', accent: '#3DA5FF', second: '#FF3DCB' },
  { from: '#0A2C3A', to: '#05080D', accent: '#22E5D4', second: '#9B5CFF' },
  { from: '#3A2206', to: '#05080D', accent: '#FFD23D', second: '#FF3D5A' },
  { from: '#2A0A44', to: '#05080D', accent: '#C04CFF', second: '#FF3DCB' },
  { from: '#3A1806', to: '#05080D', accent: '#FF8A3D', second: '#22E5D4' },
  { from: '#0A3A3E', to: '#05080D', accent: '#FFD23D', second: '#22E5D4' },
];

function figure(index: number, p: Palette, glow: string, grad: string): ReactNode {
  const g = `url(#${glow})`;
  switch (index) {
    case 1:
      return (
        <>
          <path d="M30 86C26 50 40 22 64 22S102 50 98 86" fill="#0B1020" stroke={p.second} strokeWidth="3" />
          <ellipse cx="64" cy="58" rx="20" ry="24" fill={face} stroke={p.accent} strokeWidth="2" />
          <rect x="40" y="49" width="48" height="12" rx="6" fill={p.accent} filter={g} />
          <rect x="45" y="53" width="38" height="2" rx="1" fill="#FFFFFF" opacity="0.7" />
          <path d="M56 72H72" stroke={p.accent} strokeWidth="2" opacity="0.6" />
        </>
      );
    case 2:
      return (
        <>
          <polygon points="48,44 52,14 57,36 62,8 66,34 71,10 75,36 80,16 80,46" fill={p.accent} filter={g} />
          <ellipse cx="64" cy="60" rx="19" ry="23" fill={face} stroke={p.accent} strokeWidth="2" />
          <rect x="51" y="56" width="9" height="3" rx="1.5" fill={p.second} filter={g} />
          <rect x="68" y="56" width="9" height="3" rx="1.5" fill={p.second} filter={g} />
          <path d="M48 65L54 67M80 65L74 67M58 74H70" stroke={p.accent} strokeWidth="2" opacity="0.8" />
          <circle cx="45" cy="68" r="2.5" fill="none" stroke={p.second} strokeWidth="1.5" />
        </>
      );
    case 3:
      return (
        <>
          <path d="M42 54C42 32 54 26 66 26S88 34 86 54" fill="#1D1033" stroke={p.accent} strokeWidth="2" />
          <ellipse cx="64" cy="60" rx="20" ry="24" fill={face} stroke={p.accent} strokeWidth="2" />
          <circle cx="55" cy="58" r="2.6" fill="#FFFFFF" />
          <circle cx="73" cy="58" r="7" fill="#06202A" stroke={p.second} strokeWidth="2" filter={g} />
          <circle cx="73" cy="58" r="2.4" fill={p.second} />
          <path d="M73 47V50M73 66V69M62 58H65M81 58H84" stroke={p.second} strokeWidth="1.5" />
          <path d="M80 64L86 70V80L78 84" fill="none" stroke={p.accent} strokeWidth="2" />
          <path d="M57 74H69" stroke={p.accent} strokeWidth="2" opacity="0.6" />
        </>
      );
    case 4:
      return (
        <>
          <ellipse cx="64" cy="58" rx="22" ry="26" fill={face} stroke={p.accent} strokeWidth="2" />
          <circle cx="55" cy="54" r="8" fill="#0A2A1C" stroke={p.accent} strokeWidth="2.5" />
          <circle cx="73" cy="54" r="8" fill="#0A2A1C" stroke={p.accent} strokeWidth="2.5" />
          <circle cx="55" cy="54" r="4" fill={p.second} opacity="0.8" filter={g} />
          <circle cx="73" cy="54" r="4" fill={p.second} opacity="0.8" filter={g} />
          <path d="M54 66H74L70 82H58Z" fill="#0F1A14" stroke={p.accent} strokeWidth="2" />
          <path d="M60 71H68M60 75H68" stroke={p.accent} strokeWidth="1.5" opacity="0.7" />
          <circle cx="46" cy="76" r="6" fill="#0F1A14" stroke={p.accent} strokeWidth="2" />
          <circle cx="82" cy="76" r="6" fill="#0F1A14" stroke={p.accent} strokeWidth="2" />
        </>
      );
    case 5:
      return (
        <>
          <path d="M84 36L95 18" stroke={p.accent} strokeWidth="2" />
          <circle cx="95" cy="18" r="3" fill={p.second} filter={g} />
          <path
            d="M40 68C40 42 50 30 64 30S88 42 88 68V76H40Z"
            fill="#101A2A"
            stroke={p.accent}
            strokeWidth="2.5"
          />
          <rect x="44" y="51" width="40" height="7" rx="3.5" fill={p.accent} filter={g} />
          <path d="M50 54.5H78" stroke="#05080D" strokeWidth="2" strokeDasharray="3 3" />
          <path d="M44 76H84L78 86H50Z" fill="#0B1220" stroke={p.accent} strokeWidth="2" />
        </>
      );
    case 6:
      return (
        <>
          <rect x="44" y="32" width="40" height="50" rx="18" fill={face} stroke={p.accent} strokeWidth="2" />
          <path
            d="M50 44H58V50M78 42H70V48M48 72H56L60 76M80 72H72L68 76M64 32V44"
            fill="none"
            stroke={p.accent}
            strokeWidth="1.5"
            opacity="0.7"
          />
          <circle cx="58" cy="50" r="1.6" fill={p.accent} />
          <circle cx="70" cy="48" r="1.6" fill={p.accent} />
          <ellipse cx="56" cy="60" rx="4.5" ry="3" fill={p.second} filter={g} />
          <ellipse cx="72" cy="60" rx="4.5" ry="3" fill={p.second} filter={g} />
          <rect x="56" y="82" width="16" height="10" fill={skin} stroke={p.accent} strokeWidth="1.5" />
        </>
      );
    case 7:
      return (
        <>
          <path
            d="M64 38C54 24 44 18 32 16C42 24 50 31 56 40M64 38C74 24 84 18 96 16C86 24 78 31 72 40"
            fill="none"
            stroke={p.accent}
            strokeWidth="3"
            filter={g}
          />
          <path
            d="M38 66C38 42 50 34 64 34S90 42 90 66H38Z"
            fill="#1A1410"
            stroke={p.accent}
            strokeWidth="2.5"
          />
          <path d="M40 68L28 82M88 68L100 82" stroke={p.accent} strokeWidth="2" opacity="0.6" />
          <path d="M44 66H84L80 84H48Z" fill="#120C0C" stroke={p.second} strokeWidth="2" />
          <rect x="48" y="59" width="32" height="3" rx="1.5" fill={p.second} filter={g} />
          <path d="M56 74H72" stroke={p.second} strokeWidth="1.5" opacity="0.7" />
        </>
      );
    case 8:
      return (
        <>
          <path d="M42 50C42 32 54 28 64 28S86 32 86 50" fill="#1A0A2E" stroke={p.accent} strokeWidth="2" />
          <ellipse cx="64" cy="60" rx="20" ry="24" fill={face} stroke={p.accent} strokeWidth="2" />
          <path d="M38 56H28M90 56H100" stroke={p.accent} strokeWidth="3" />
          <rect x="38" y="47" width="52" height="18" rx="8" fill={`url(#${grad})`} filter={g} />
          <path d="M45 51H70" stroke="#FFFFFF" strokeWidth="1.5" opacity="0.6" />
          <path d="M58 76Q64 80 70 76" fill="none" stroke={p.accent} strokeWidth="2" opacity="0.7" />
        </>
      );
    case 9:
      return (
        <>
          <ellipse cx="64" cy="62" rx="20" ry="24" fill={face} />
          <path
            d="M40 28L53 46H75L88 28L86 58C86 71 76 80 64 86C52 80 42 71 42 58Z"
            fill="#1A0F0A"
            stroke={p.accent}
            strokeWidth="2.5"
            filter={g}
          />
          <path d="M44 35L52 46M84 35L76 46" stroke={p.second} strokeWidth="1.5" />
          <path d="M50 57L60 60M78 57L68 60" stroke={p.second} strokeWidth="3" filter={g} />
          <path d="M64 64V78M52 70L58 72M76 70L70 72" stroke={p.accent} strokeWidth="1.5" opacity="0.7" />
        </>
      );
    default:
      return (
        <>
          <path d="M64 34V21" stroke={p.accent} strokeWidth="2" />
          <circle cx="64" cy="19" r="3" fill={p.second} filter={g} />
          <rect x="36" y="48" width="6" height="14" rx="2" fill={skin} stroke={p.accent} strokeWidth="1.5" />
          <rect x="86" y="48" width="6" height="14" rx="2" fill={skin} stroke={p.accent} strokeWidth="1.5" />
          <rect
            x="42"
            y="34"
            width="44"
            height="44"
            rx="7"
            fill="#101A24"
            stroke={p.accent}
            strokeWidth="2.5"
          />
          <circle cx="55" cy="52" r="6" fill="none" stroke={p.second} strokeWidth="2" />
          <circle cx="73" cy="52" r="6" fill="none" stroke={p.second} strokeWidth="2" />
          <circle cx="55" cy="52" r="2.5" fill={p.second} filter={g} />
          <circle cx="73" cy="52" r="2.5" fill={p.second} filter={g} />
          <rect x="52" y="64" width="24" height="7" rx="2" fill="none" stroke={p.second} strokeWidth="1.5" />
          <path d="M57 64V71M62 64V71M66 64V71M71 64V71" stroke={p.second} strokeWidth="1" />
        </>
      );
  }
}

export function avatarIndex(value: number | null | undefined): number {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n < 1) return 1;
  return ((n - 1) % avatarCount) + 1;
}

export function CyberAvatar({ index, size = 40, label }: { index: number; size?: number; label?: string }) {
  const n = avatarIndex(index);
  const p = palettes[n - 1] as Palette;
  const id = useId().replace(/:/g, '');
  const ref = (name: string) => `qs-avatar-${name}-${id}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 128 128"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-avatar={n}
    >
      <defs>
        <radialGradient id={ref('bg')} cx="0.5" cy="0.38" r="0.75">
          <stop offset="0" stopColor={p.from} />
          <stop offset="1" stopColor={p.to} />
        </radialGradient>
        <linearGradient id={ref('grad')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={p.second} />
          <stop offset="1" stopColor={p.accent} />
        </linearGradient>
        <clipPath id={ref('clip')}>
          <circle cx="64" cy="64" r="64" />
        </clipPath>
        <filter id={ref('glow')} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <pattern id={ref('scan')} width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="1" fill="#FFFFFF" opacity="0.06" />
        </pattern>
      </defs>
      <g clipPath={`url(#${ref('clip')})`}>
        <rect width="128" height="128" fill={`url(#${ref('bg')})`} />
        <path
          d="M0 100H128M0 114H128M16 128L40 96M48 128L56 96M80 128L72 96M112 128L88 96"
          stroke={p.accent}
          strokeWidth="1"
          opacity="0.18"
        />
        <path d="M14 128C18 103 38 93 64 93S110 103 114 128Z" fill={skin} stroke={p.accent} strokeWidth="2" />
        <path d="M54 93L64 104L74 93" fill="none" stroke={p.second} strokeWidth="1.5" opacity="0.8" />
        {figure(n, p, ref('glow'), ref('grad'))}
        <rect width="128" height="128" fill={`url(#${ref('scan')})`} />
      </g>
      <circle cx="64" cy="64" r="63" fill="none" stroke={p.accent} strokeOpacity="0.45" strokeWidth="2" />
    </svg>
  );
}
